import React, { useEffect, useState } from "react"
import { AiOutlineCloudUpload } from "react-icons/ai"
import { BsTrash2 } from "react-icons/bs"
import { FiPlusCircle, FiEye, FiMessageCircle } from "react-icons/fi"
import Spinner from "../../components/Spinner"
import DashboardHeader from "../../components/DashboardHeader"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import RenderNoFarmer from "../../components/NoData/RenderNoFarmer"
import { Tooltip } from "../../layout/sidebar"
import { RxPencil1 } from "react-icons/rx"
import api, { fetchAll } from "../../utils/api"
import { SuccessNotification, ErrorNotification } from "../../utils"
import FarmerSMSMoal from "../../components/modals/FarmerSMSMoal"
import UploadMillerModal from "../../components/modals/UploadMillerModal"
import { useHistory } from "react-router-dom"
import Pagination from "../../components/Pagination"
import FarmerDetailDrawer from "../../components/Drawers/FarmerDetailDrawer"
import DeleteModal from "../../components/DeleteModal"
import LeafletMap from "../../components/LeafletMap"
import useAuth from "../../context/userContext"

const Index = () => {
  const history = useHistory()
  const { user, permissions } = useAuth()
  const [farmers, setFarmers] = useState([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSMSModal, setShowSMSModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [view, setView] = useState("list")
  const [isOpen, setIsOpen] = useState(false)

  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)

  const getAllFarmers = async () => {
    setLoading(true)
    try {
      const resp = await api.get("farmers/", {
        params: {
          page: currentPage,
          page_size: itemsPerPage,
          search: debouncedSearch || undefined,
          region: states.region !== "Select Region" ? states.region : undefined,
          district: states.district !== "Select District" ? states.district : undefined,
          community: states.community !== "Select Community" ? states.community : undefined,
        },
      })
      setFarmers(resp.data.results || [])
      setCount(resp.data.count || 0)
    } catch (error) {
      ErrorNotification("Failed to get farmers")
    } finally {
      setLoading(false)
    }
  }

  // Frontend filtering logic is now handled by getAllFarmers with backend params
  const filterFarmers = (searchTerm = search) => {
    setCurrentPage(1)
    getAllFarmers()
  }

  function handleChange(evt) {
    setState({
      ...states,
      [evt.target.name]: evt.target.value,
    })
  }

  const resetFilters = () => {
    setSearch("")
    setState(initialState)
    setCurrentPage(1)
  }

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(farmers.map(f => f.id))
    } else {
      setSelectedIds([])
    }
  }

  const toggleSelectId = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const handleDelete = async (id) => {
    try {
      await api.delete(`farmers/${id}/`)
      SuccessNotification("Farmer deleted successfully")
      getAllFarmers()
      setShowConfirmModal(false)
    } catch (error) {
      ErrorNotification("Failed to delete farmer")
    }
  }

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true)
    try {
      await api.delete("farmers/bulk_delete/", {
        data: { farmer_ids: selectedIds }
      })
      SuccessNotification(`${selectedIds?.length} farmers deleted successfully`)
      setSelectedIds([])
      getAllFarmers()
      setShowConfirmModal(false)
    } catch (error) {
      ErrorNotification("Failed to delete some farmers")
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = farmers
  const totalPages = Math.ceil(count / itemsPerPage)

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum)
  }

  const handleNewGroup = () => {
    history.push("/admin/newFarmer")
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    getAllFarmers()
  }, [currentPage, states, debouncedSearch])

  return (
    <>
      <FarmerDetailDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        farmer={selected}
      />

      {showConfirmModal && <DeleteModal
        setShowConfirmModal={setShowConfirmModal}
        showConfirmModal={showConfirmModal}
        title={selectedIds.length > 1 ? `${selectedIds.length} farmers` : "farmer"}
        loading={loading || isBulkDeleting}
        handleDelete={() => {
          if (selectedIds.length > 1) {
            handleBulkDelete()
          } else {
            handleDelete(selected?.id || selectedIds[0])
          }
        }}
      />}

      {showUploadModal &&
        <UploadMillerModal
          userType={user?.user_type}
          onClose={() => setShowUploadModal(false)}
          mutate={getAllFarmers}
        />
      }

      {showSMSModal && <FarmerSMSMoal
        onClose={() => setShowSMSModal(false)}
        userType={selected}
      />}

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="My Farmers" />
            <div className="flex flex-col md:flex-row gap-2">
              {permissions?.capabilities?.canAddFarmers && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="border border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md"
                >
                  <AiOutlineCloudUpload size={20} />
                  Upload farmers
                </button>
              )}
              {permissions?.capabilities?.canAddFarmers && (
                <button
                  onClick={handleNewGroup}
                  className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
                >
                  <FiPlusCircle size={20} />
                  Add new farmer
                </button>
              )}
              {selectedIds.length > 0 && permissions?.capabilities?.canDeleteFarmers && (
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
                >
                  <BsTrash2 size={20} />
                  Delete Selected ({selectedIds.length})
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FilterWithSearchAndRegion
              placeholder="Search farmer"
              suggestionsUrl="farmers/"
              onSearch={(searchTerm) => {
                setSearch(searchTerm)
                setCurrentPage(1)
              }}
              view={view}
              setView={setView}
              handleChange={handleChange}
              hideMap={true}
              setSearch={setSearch}
              search={search}
            />
            {(search || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : view === "map" ? (
            <LeafletMap farmers={farmers || []} />
          ) : (
            <div className="my-5">
              {farmers?.length > 0 ? (
                <div className="flex flex-col">
                  <div className="-m-1.5 overflow-x-auto">
                    <div className="p-1.5 min-w-full inline-block align-middle">
                      <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                          <thead className="bg-transparent dark:bg-secondaryDark">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-start">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor cursor-pointer"
                                  checked={farmers.length > 0 && selectedIds.length === farmers.length}
                                  onChange={toggleSelectAll}
                                />
                              </th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Name</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Gender</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Phone No./ Email</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">No. of Farms</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Region</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">District</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Community</th>
                              <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                            {farmers?.map((data) => (
                              <tr key={data.id} className="text-[13px]">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(data.id)}
                                    onChange={() => toggleSelectId(data.id)}
                                    className="rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor cursor-pointer"
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2">
                                  {data?.profile_image ? (
                                    <div className="w-10 h-10 overflow-hidden shrink-0">
                                      <img src={data?.profile_image} alt="" className="w-full h-full object-cover rounded-full" />
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 shrink-0">
                                      <p className="text-sm uppercase font-bold text-gray-600">{data?.firstname?.charAt(0)}{data?.lastname?.charAt(0)}</p>
                                    </div>
                                  )}
                                  <p>{`${data.firstname} ${data.lastname}`}</p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap capitalize">{data.gender || "N/A"}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex flex-col">
                                    <p className="capitalize">{data.phone || "N/A"}</p>
                                    <p className="lowercase text-xs">{data.email || "N/A"}</p>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">{data.farms_count || 0}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{data.region || "N/A"}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{data.district || "N/A"}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{data.community || "N/A"}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-end">
                                  <div className="flex flex-row items-center justify-end gap-3">
                                    <Tooltip text="View Details" position="top">
                                      <button onClick={() => { setSelected(data); setIsOpen(true) }}>
                                        <FiEye size={18} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Add Farm" position="top">
                                      <button onClick={() => history.push("/admin/newfarm", { farmer: data })}>
                                        <FiPlusCircle size={18} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Send SMS" position="top">
                                      <button onClick={() => { setSelected(data); setShowSMSModal(true) }}>
                                        <FiMessageCircle size={18} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Update" position="top">
                                      <button onClick={() => history.push("/admin/updateFarmer", { farmer: data })}>
                                        <RxPencil1 size={18} />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Delete" position="top">
                                      <button onClick={() => { setSelected(data); setShowConfirmModal(true) }}>
                                        <BsTrash2 size={18} className="text-red-500" />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <RenderNoFarmer
                  isSearch={search?.trim()?.length > 0 || (states.region || states.district || states.community)}
                  handleNewGroup={handleNewGroup}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Index
